import { useContext } from 'react';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _isNil from 'lodash/isNil';
import _isEmpty from 'lodash/isEmpty';
import _isNaN from 'lodash/isNaN';

import { AjaxContext, AppContext } from '../../../../context';
import { AssetType, END_POINT, FORM_DATA_NAME, URL_NAME } from './constants';

const useService = () => {
	const { postData, putData } = useContext(AjaxContext);
	const { app, showAlert } = useContext(AppContext);

	const convertMegaByteToByte = (fileSize: number) => {
		return fileSize * 1000000;
	};

	const uploadToS3 = async (
		fileToUpload: File,
		assetType: AssetType,
		maxFileSizeMB?: number,
		subFolder?: string,
		zupeeUserId?: number,
		usePresignedUrl: boolean = false
	) => {
		const formData = new FormData();
		if (!_isNil(maxFileSizeMB) && maxFileSizeMB !== 0 && fileToUpload.size > convertMegaByteToByte(maxFileSizeMB)) {
			showAlert(`Failed to upload ,file size is larger than ${maxFileSizeMB} MB`, { type: 'error' });
			return;
		}

		formData.append(FORM_DATA_NAME[assetType], fileToUpload);
		formData.append('dashboard', 'crm');
		if (!_isNil(subFolder) && !_isEmpty(subFolder)) {
			formData.append('subFolder', subFolder);
		}
		if (!_isNil(zupeeUserId) && !_isNaN(zupeeUserId)) {
			formData.append('zupeeUserId', `${zupeeUserId}`);
		}

		try {
			if (usePresignedUrl) {
				const preSignedUrlResponse: { success: boolean; signedUrl: string } = await postData({
					endpoint: END_POINT[assetType],
					body: {
						mimetype: fileToUpload.name.split('.').pop(),
						assetType
					},
					queryParams: {
						appName: app.appName
					},
					timeout: 300000
				});
				const signedUrl: string = _get(preSignedUrlResponse, ['data', URL_NAME[assetType]]);
				await putData({
					endpoint: signedUrl,
					body: fileToUpload,
					timeout: 300000,
					customHeaders: {
						'Content-Type': fileToUpload.type
					}
				});
				return { assetUrl: signedUrl.split('?')[0], fileName: fileToUpload.name };
			}
			const result = await postData({
				endpoint: END_POINT[assetType],
				body: formData,
				customHeaders: { 'Content-Type': 'multipart/form-data' },
				queryParams: {
					appName: app.appName
				},
				timeout: 300000
			});

			if (result.status !== 200 || !result.data.success) {
				showAlert(`${result.data.error}`, { type: 'error' });
				return undefined;
			}
			return { assetUrl: _get(result, ['data', URL_NAME[assetType]]), fileName: fileToUpload.name };
		} catch (err: any) {
			showAlert(`${err?.response?.data?.error}`, { type: 'error' });
			return undefined;
		}
	};

	const uploadMultipleToS3 = async (filesToUpload: File[], assetType: AssetType) => {
		return Promise.all(_map(filesToUpload, async (fileToUpload) => uploadToS3(fileToUpload, assetType)));
	};

	return {
		uploadToS3,
		uploadMultipleToS3
	};
};

export default useService;
